import { KeyboardArrowDown } from "@mui/icons-material";

import AutoCompleteDropdown from "../../../../../ui library/components/dropdowns/AutoCompleteDropdown";
import HNSColors from "../../../../../ui library/HNSColors";
import { useProviderTypes } from "../../hooks/useProviderTypes";
import { useProviderSearchStore } from "../../store/providerSearchStore";
import STYLES from "../../style/style";

import CustomListBox from "./CustomListBox";

export default function ProviderTypeSearchField({ placeholder }) {
  const { data, setSearch, loading, showMore, handleShowMore } = useProviderTypes();
  const {
    providerType,
    providerTypeMatchAll,
    setAssignedProvider,
    setProviderType,
    setProviderTypeMatchAll,
    resetCurrentPage,
    setInNetwork,
  } = useProviderSearchStore();
  const onValueChange = (value) => {
    if (!value || value.length === 0) {
      setInNetwork(1);
    }
    // The match mode is only shown, and only meaningful, with 2+ types. Clear it
    // below that so a hidden "match all" can't silently re-apply when the user
    // adds a second type again.
    if (!value || value.length < 2) {
      setProviderTypeMatchAll(false);
    }
    setAssignedProvider(null);
    setProviderType(value);
    resetCurrentPage();
  };

  const onMatchModeChange = (event) => {
    // Changing the match mode changes the result set, so drop any row the user
    // picked out of the old one, as the other filter handlers do.
    setAssignedProvider(null);
    setProviderTypeMatchAll(event.target.value === "all");
    resetCurrentPage();
  };

  return (
    <>
      <AutoCompleteDropdown
        data={data.map((type) => ({ id: type.id, label: type.label }))}
        initValue={null}
        multiple
        getOptionLabel={(option) => option?.label || ""}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onValueChange={(_, value) => {
          onValueChange(value);
        }}
        onInputChange={(_, value) => setSearch(value)}
        placeholder={placeholder}
        ListboxComponent={CustomListBox}
        ListboxProps={{
          showMore: showMore,
          setShowMore: handleShowMore,
          sx: { overflow: "auto", maxHeight: 200 },
        }}
        dropdownSx={STYLES.selectMenuPaper}
        selectProps={{ IconComponent: (props) => <KeyboardArrowDown {...props} /> }}
        loading={loading}
        textFieldSx={{
          ...STYLES.textField,
          "& .MuiOutlinedInput-root": {
            width: "100%",
            padding: "7.5px",
            borderRadius: "8px",
            "& fieldset": { borderColor: HNSColors.neutrals[400] },
            "&:hover fieldset": { border: `1.5px solid ${HNSColors.secondary[500]}` },
            "&.Mui-focused fieldset": { border: `1.5px solid ${HNSColors.secondary[500]}` },
            "& .MuiAutocomplete-inputRoot": {
              flexWrap: "nowrap",
              overflowX: "auto",
              "&::-webkit-scrollbar": {
                height: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: HNSColors.primary[500],
                borderRadius: "3px",
              },
            },
          },
          "& .MuiAutocomplete-endAdornment": {
            right: "4px",
            flexShrink: 0,
          },
        }}
        size="small"
      />
      {/* Only meaningful with 2+ types selected; with one, "all" and "any" agree. */}
      {providerType?.length > 1 && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span
            style={{
              color: HNSColors.neutrals[400],
              fontSize: "0.76rem",
              fontWeight: 600,
            }}
          >
            Search logic:
          </span>
          <div
            className="inline-flex flex-wrap gap-2"
            role="group"
            aria-label="Provider type match mode"
          >
            <button
              id="provider-type-match-any-button"
              type="button"
              aria-pressed={!providerTypeMatchAll}
              onClick={() => onMatchModeChange({ target: { value: "any" } })}
              className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-left transition-colors duration-150"
              style={{
                borderColor: !providerTypeMatchAll
                  ? HNSColors.secondary[700]
                  : HNSColors.secondary[900],
                backgroundColor: !providerTypeMatchAll
                  ? HNSColors.secondary[950]
                  : HNSColors.neutrals[900],
                color: !providerTypeMatchAll ? HNSColors.secondary[300] : HNSColors.neutrals[300],
              }}
            >
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{
                  backgroundColor: !providerTypeMatchAll
                    ? HNSColors.secondary[500]
                    : HNSColors.secondary[800],
                }}
              />
              <span className="text-[0.82rem] font-semibold">Any selected type</span>
              <span
                className="text-[0.72rem]"
                style={{
                  color: !providerTypeMatchAll
                    ? HNSColors.secondary[500]
                    : HNSColors.neutrals[500],
                }}
              >
                Broader results
              </span>
            </button>
            <button
              id="provider-type-match-all-button"
              type="button"
              aria-pressed={providerTypeMatchAll}
              onClick={() => onMatchModeChange({ target: { value: "all" } })}
              className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-left transition-colors duration-150"
              style={{
                borderColor: providerTypeMatchAll
                  ? HNSColors.secondary[700]
                  : HNSColors.secondary[900],
                backgroundColor: providerTypeMatchAll
                  ? HNSColors.secondary[950]
                  : HNSColors.neutrals[900],
                color: providerTypeMatchAll
                  ? HNSColors.secondary[300]
                  : HNSColors.neutrals[300],
              }}
            >
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{
                  backgroundColor: providerTypeMatchAll
                    ? HNSColors.secondary[500]
                    : HNSColors.secondary[800],
                }}
              />
              <span className="text-[0.82rem] font-semibold">All selected types</span>
              <span
                className="text-[0.72rem]"
                style={{
                  color: providerTypeMatchAll
                    ? HNSColors.secondary[500]
                    : HNSColors.neutrals[500],
                }}
              >
                More precise results
              </span>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
